import React, { JSX, PropsWithChildren, useState } from 'react'
import './index.scss'
import { observer } from 'mobx-react'
import { FormProvider, useForm } from 'react-hook-form'
import { QueryKey, useIsFetching } from '@tanstack/react-query'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import { FilterFieldGroup } from '@App/forms/common/filter-field-group'
import { mapCriteriaToObject } from '@App/helpers/map-criteria-to-object'
import { SearchWithCriteriaFormType } from '@Components/Search/SearchWithCriteria/form-type'
import SearchControlSection from '@Components/Search/SearchWithCriteria/SearchControlSection'
import SearchCriteriaSection from '@Components/Search/SearchWithCriteria/SearchCriteriaSection'
import { FilterValues, SearchPanelSortOption } from '@Components/Search/SearchPanel/types'
import SearchFilter from '@Components/Search/SearchPanel/SearchFilter'
import { stripEmptyFilters } from '@Components/Search/SearchPanel/utils'

export interface SearchPanelProps<T extends object, K extends object> {
    searchCriteriaStore: SearchCriteriaStore<T>
    /**
     * Called with the submitted criteria and filters. Implementations are expected to update the
     * state that feeds `searchQueryKey`; the request itself is made by TanStack Query.
     */
    onSearch: (submittedSearchCriteria: Partial<T>, filters: Partial<K>) => void
    /**
     * Key (or key prefix) of the TanStack Query this panel drives. The search controls are
     * disabled for as long as a matching query is in flight.
     */
    searchQueryKey?: QueryKey
    onReset?: () => void
    onApplyFilters?: (filters: FilterValues<K> | undefined) => void
    onFilterValueChange?: (filters: FilterValues<K> | undefined) => void
    canAddCriteria?: boolean
    filterFieldCollections?: Array<FilterFieldGroup<K>>
    defaultFilterValues?: FilterValues<K>
    orientation?: 'column' | 'row'
    extraButtons?: JSX.Element[]
    isUniqueTooltip?: JSX.Element
    disableAutoComplete?: boolean
    disabled?: boolean
    sortOptions?: SearchPanelSortOption[]
    /**
     * Ties the SortBy dropdown to this field of the filter values (K), keeping it in sync with a
     * same-named field inside filterFieldCollections (e.g. a radio group): picking a value in
     * either control applies it through the same apply-filters flow. Takes precedence over
     * sortValue/onSortChange.
     */
    sortFilterKey?: keyof K & string
    /** Manual sort control for pages with no equivalent filter field. Ignored if sortFilterKey is set. */
    sortValue?: string
    onSortChange?: (value: string) => void
}

/**
 * Criteria based search panel backed by TanStack Query.
 *
 * Replacement for `SearchWithCriteria`: instead of owning a `loading` flag, it observes the
 * search query it drives, which keeps consumers free of loading state plumbing.
 *
 * @template {object} T - The record type for the "criteria" portion of the form
 * @template {object} K - The record type for the "filter" portion of the form
 */
const SearchPanel = <T extends object, K extends object>(props: PropsWithChildren<SearchPanelProps<T, K>>) => {
    const {
        searchCriteriaStore,
        onSearch,
        searchQueryKey,
        onReset,
        onApplyFilters,
        onFilterValueChange,
        canAddCriteria,
        filterFieldCollections,
        defaultFilterValues,
        orientation = 'column',
        extraButtons,
        isUniqueTooltip,
        disableAutoComplete,
        disabled,
        sortOptions,
        sortFilterKey,
        sortValue,
        onSortChange,
    } = props

    const fetchingCount = useIsFetching({ queryKey: searchQueryKey ?? ['__search_panel_unbound__'] })
    const disableSearchButtons = disabled || fetchingCount > 0

    const methods = useForm<SearchWithCriteriaFormType<K>>({
        defaultValues: { search: [] as Array<SearchCriterion<T>>, filter: defaultFilterValues },
    })

    // SearchFilter owns its own applied-filter state (drives the "Filters (N)" badge, pills, and
    // the SortBy dropdown), separate from the form fields reset below. Remounting it on reset is
    // the simplest way to fully re-initialize that state from defaultFilterValues too.
    const [filterResetKey, setFilterResetKey] = useState(0)

    const handleReset = () => {
        if (onReset) {
            onReset()
            methods.reset({ search: [], filter: defaultFilterValues ?? undefined })
            setFilterResetKey((key) => key + 1)
        }
    }

    /* Process data from arrays of SearchCriterion to the given generic datastructure (T) */
    const onSubmit = (data: SearchWithCriteriaFormType<K>) => {
        const filter = stripEmptyFilters(data.filter)
        searchCriteriaStore.syncFormValues(data.search)
        onSearch({ ...mapCriteriaToObject<T>(data.search) }, { ...filter })
    }

    return (
        <div className="search-criteria">
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)} aria-label="Search Form">
                    <SearchCriteriaSection
                        searchCriteriaStore={searchCriteriaStore}
                        canAddCriteria={canAddCriteria}
                        orientation={orientation}
                        isUniqueTooltip={isUniqueTooltip}
                        disableAutoComplete={disableAutoComplete}
                    />
                    <SearchControlSection<K>
                        searchCriteriaStore={searchCriteriaStore}
                        resetSearchFn={onReset ? handleReset : undefined}
                        disableSearchButtons={disableSearchButtons}
                        extraButtons={extraButtons}
                    />
                    <SearchFilter
                        key={filterResetKey}
                        filterFieldCollections={filterFieldCollections}
                        onFilterValueChange={onFilterValueChange}
                        onApplyFilters={onApplyFilters}
                        methods={methods}
                        onSubmit={onSubmit}
                        defaultFilterValues={defaultFilterValues}
                        sortOptions={sortOptions}
                        sortFilterKey={sortFilterKey}
                        sortValue={sortValue}
                        onSortChange={onSortChange}
                    />
                </form>
            </FormProvider>
        </div>
    )
}

export default observer(SearchPanel)
